import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { MyTeamsService } from 'src/app/services/my-teams/my-teams.service';
import { UserService } from 'src/app/services/general/user.service';
import { ITeamModuleAssignment } from 'src/app/interfaces/iteam-module-assignment';
import { IAssignment } from 'src/app/interfaces/iassignment';
import { ITeamMember } from 'src/app/interfaces/iteam-member';
import { ITeamMemberDetails } from 'src/app/interfaces/iteam-member-details';
import { IProgressOverview } from 'src/app/interfaces/iprogress-overview';
import { IModuleAssignmentSummary } from 'src/app/interfaces/imodule-assignment-summary';
import { ICallCount } from 'src/app/interfaces/icall-count';
import { ICallSummary } from 'src/app/interfaces/icall-summary';
import { ISummaryCall } from 'src/app/interfaces/isummary-call';
import { Chart } from 'chart.js/auto';
import { MatDialog } from '@angular/material/dialog';
import { MyTeamModalComponent } from 'src/app/pages/team-admin/my-teams/my-team-modal/my-team-modal.component';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import { localeUpperCase } from 'src/app/utils/locale-case.util';
@Component({
  selector: 'app-my-teams',
  standalone: false,
  templateUrl: './my-teams.component.html',
  styleUrls: ['./my-teams.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'teams',
    },
  ],
})
export class MyTeamsComponent implements OnInit {
  assignments: IAssignment[] = [];
  selectedModuleAssignment: IAssignment | null = {
    moduleAssignmentUuid: '',
    name: '',
    dueDate: '',
  };
  selectedTeam: ITeamModuleAssignment = {
    teamUuid: '',
    name: '',
    moduleAssignments: [
      {
        moduleAssignmentUuid: '',
        name: '',
        dueDate: '',
      },
    ],
  };
  tab: string = 'completed';
  calls_in_module: number = 3;
  completion_requirement: number = 4;
  teamMembers: ITeamMember[] = [];
  teams: ITeamModuleAssignment[] = [];
  completedModule: IProgressOverview = { percentage: 0, membersQuantity: 0 };
  inProgressModule: IProgressOverview = { percentage: 0, membersQuantity: 0 };
  notStartedModule: IProgressOverview = { percentage: 0, membersQuantity: 0 };
  moduleAssignmentSummary: IModuleAssignmentSummary = {
    moduleAssignmentUuid: '',
    name: '',
    instructions: '',
    completionRequirement: 0,
    startDate: '',
    dueDate: '',
    assignedMembers: 0,
    callsInModuleAssignment: 0,
    averageScore: 0,
    averageAttemptsPerCall: 0,
    totalPractices: 0,
    progressOverview: {
      completed: { percentage: 0, membersQuantity: 0 },
      inProgress: { percentage: 0, membersQuantity: 0 },
      notStarted: { percentage: 0, membersQuantity: 0 },
    },
  };
  teamUuid: string = '';
  moduleAssignmentUuid: string = '';
  teamCallCount: ICallCount = {
    totalPracticesPerDay: [{ practiceCount: 0, day: '' }],
    practiceRateSummary: { practiceCount: 0, averagePerDay: 0 },
  };
  summaryCalls: ISummaryCall[] = [];

  SORT_NONE: string = 'none';
  SORT_ASC: string = 'asc';
  SORT_DESC: string = 'desc';
  ARROW: string = 'arrow';
  ARROW_UP: string = 'arrow-up';
  ARROW_DOWN: string = 'arrow-down';

  sortName: string = 'none';
  sortNameClass: string = 'arrow';
  sortAssignementProgress: string = 'none';
  sortAssignementProgressClass: string = 'arrow';
  sortCallsCompleted: string = 'none';
  sortCallsCompletedClass: string = 'arrow';
  sortPractices: string = 'none';
  sortPracticesClass: string = 'arrow';
  sortScore: string = 'none';
  sortScoreClass: string = 'arrow';
  sortMostRecentActivity: string = 'none';
  sortMostRecentActivityClass: string = 'arrow';
  lineChart: any;
  lineChartLabels: string[] = [];
  lineChartDataSet: number[] = [];
  monthNames: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  isLoadingAssignments: boolean = true;
  userUuid: string = '';

  constructor(
    private dialog: MatDialog,
    private myTeamsService: MyTeamsService,
    private router: Router,
    private route: ActivatedRoute,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    protected localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('teams');
    this.amplitudeService.trackEvent('myteams_pageview');
    localStorage.setItem('returnUrl', '/team_manager/my_teams');

    window.setTimeout(() => {
      const queryParams = this.route.snapshot.queryParams;
      if (Object.keys(queryParams).length > 0) {
        this.handleQueryParams();
      } else {
        this.getSelectedTeamAndModuleAssignment();
        this.getTeamsAndModuleAssignments();
      }
    });
  }

  handleQueryParams(): void {
    const params = this.route.snapshot.queryParams;
    this.userUuid = params['user_uuid'];
    this.validateAccessToken();
  }

  validateAccessToken(): void {
    const user_uuid = localStorage.getItem('user_uuid') || '';
    if (user_uuid !== '' && user_uuid !== this.userUuid) {
      this.router.navigate(['/login'], { replaceUrl: true });
    } else {
      this.getTeamsAndModuleAssignments();
    }
  }

  getTeamsAndModuleAssignments() {
    this.myTeamsService
      .getTeamsAndModuleAssignments()
      .pipe(
        finalize(() => {
          this.isLoadingAssignments = false;
        })
      )
      .subscribe((response: ITeamModuleAssignment[]) => {
        if (response && response.length > 0) {
          this.teams = this.sortTeamsByName(response);

          // set the selected team from local storage or default to first
          this.selectedTeam =
            this.teams.find((team) => team.teamUuid === this.teamUuid) ||
            this.teams[0];
          this.teamUuid = this.selectedTeam.teamUuid;
          this.assignments = this.sortModuleAssignmentsByDueDate(
            this.selectedTeam.moduleAssignments
          );
          localStorage.setItem(
            'selected_team_uuid',
            this.selectedTeam.teamUuid
          );

          // set the selected module assignment from local storage or default to first
          this.selectedModuleAssignment =
            this.assignments.find(
              (assignment) =>
                assignment.moduleAssignmentUuid === this.moduleAssignmentUuid
            ) || this.assignments[0];
          this.moduleAssignmentUuid =
            this.selectedModuleAssignment.moduleAssignmentUuid;
          localStorage.setItem(
            'selected_module_assignment_uuid',
            this.selectedModuleAssignment.moduleAssignmentUuid
          );

          this.getModuleAssignmentSummary();
        } else {
          console.error(
            'Response is empty or null in get teams and module assignment.'
          );
        }
      });
  }

  getModuleAssignmentSummary() {
    this.myTeamsService
      .getModuleAssignmentSummary(this.teamUuid, this.moduleAssignmentUuid)
      .subscribe((response: IModuleAssignmentSummary) => {
        this.moduleAssignmentSummary = response;
        this.completedModule = response.progressOverview.completed;
        this.inProgressModule = response.progressOverview.inProgress;
        this.notStartedModule = response.progressOverview.notStarted;
      });
    // this.getCallCountPerDay();
    this.getCallSummary();
    this.getTeamMembersDetail();
  }

  getCallCountPerDay() {
    this.myTeamsService
      .getCallCountPerDay(this.teamUuid, this.moduleAssignmentUuid)
      .subscribe((response: ICallCount) => {
        this.teamCallCount = response;
        const startIndex = Math.max(
          this.teamCallCount.totalPracticesPerDay.length - 14,
          0
        );
        this.lineChartDataSet = this.teamCallCount.totalPracticesPerDay
          .slice(startIndex)
          .map((index) => index.practiceCount);
        this.lineChartLabels = this.teamCallCount.totalPracticesPerDay
          .slice(startIndex)
          .map((item) => this.formatDate(item.day));
        this.createLineChart();
      });
  }

  getCallSummary() {
    this.myTeamsService
      .getCallsSummary(this.teamUuid, this.moduleAssignmentUuid)
      .subscribe((response: ICallSummary) => {
        this.summaryCalls = response.calls;
      });
  }

  getTeamMembersActivity() {
    // Sending the Clicked 'Download Activity Report' event to Amplitude
    this.amplitudeService.trackEvent('myteams_click_download');

    this.myTeamsService
      .getTeamMembersActivity(this.teamUuid, this.moduleAssignmentUuid)
      .subscribe((response) => {
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'team_members_activity.csv';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
  }

  getTeamMembersDetail() {
    this.myTeamsService
      .getTeamMembersDetail(this.teamUuid, this.moduleAssignmentUuid)
      .subscribe((response: ITeamMemberDetails) => {
        if (response && response.teamMembers.length > 0) {
          this.teamMembers = response.teamMembers;
          this.resetFilters('');
          if (this.isProgressStatusSameForAll()) {
            this.sortCallsCompleted = this.SORT_DESC;
            this.sortCallsCompletedClass = this.ARROW_DOWN;
          } else {
            this.sortAssignementProgress = this.SORT_DESC;
            this.sortAssignementProgressClass = this.ARROW_DOWN;
          }
          this.sortTeamMembers();
        } else {
          this.teamMembers = [];
          console.error(
            'Response is empty or null in get teams members detail.'
          );
        }
      });
  }

  changeSort(type: string): void {
    this.resetFilters(type);
    switch (type) {
      case 'firstName':
        this.sortName = this.getNextSort(this.sortName);
        this.sortNameClass = this.getSortClass(this.sortName);
        break;
      case 'progressStatus':
        this.sortAssignementProgress = this.getNextSort(
          this.sortAssignementProgress
        );
        this.sortAssignementProgressClass = this.getSortClass(
          this.sortAssignementProgress
        );
        break;
      case 'callsCompleted':
        this.sortCallsCompleted = this.getNextSort(this.sortCallsCompleted);
        this.sortCallsCompletedClass = this.getSortClass(
          this.sortCallsCompleted
        );
        break;
      case 'totalPractices':
        this.sortPractices = this.getNextSort(this.sortPractices);
        this.sortPracticesClass = this.getSortClass(this.sortPractices);
        break;
      case 'recentActivityDate':
        this.sortMostRecentActivity = this.getNextSort(
          this.sortMostRecentActivity
        );
        this.sortMostRecentActivityClass = this.getSortClass(
          this.sortMostRecentActivity
        );
        break;
    }
    this.sortTeamMembers();
  }

  resetFilters(type: string): void {
    if (type !== 'firstName') {
      this.sortName = this.SORT_NONE;
      this.sortNameClass = this.ARROW;
    }
    if (type !== 'progressStatus') {
      this.sortAssignementProgress = this.SORT_NONE;
      this.sortAssignementProgressClass = this.ARROW;
    }
    if (type !== 'callsCompleted') {
      this.sortCallsCompleted = this.SORT_NONE;
      this.sortCallsCompletedClass = this.ARROW;
    }
    if (type !== 'totalPractices') {
      this.sortPractices = this.SORT_NONE;
      this.sortPracticesClass = this.ARROW;
    }
    if (type !== 'recentActivityDate') {
      this.sortMostRecentActivity = this.SORT_NONE;
      this.sortMostRecentActivityClass = this.ARROW;
    }
  }

  getNextSort(currentSort: string): string {
    switch (currentSort) {
      case this.SORT_NONE:
        return this.SORT_ASC;
      case this.SORT_ASC:
        return this.SORT_DESC;
      case this.SORT_DESC:
        return this.SORT_ASC;
    }
    return currentSort;
  }

  getSortClass(currentSort: string): string {
    switch (currentSort) {
      case this.SORT_NONE:
        return this.ARROW;
      case this.SORT_ASC:
        return this.ARROW_UP;
      case this.SORT_DESC:
        return this.ARROW_DOWN;
    }
    return this.ARROW;
  }

  sortTeamMembers(): void {
    const sortData = this.teamMembers.sort((a, b) => {
      if (this.sortName === this.SORT_ASC) {
        return (a.lastName || '').localeCompare(b.lastName || '');
      } else if (this.sortName === this.SORT_DESC) {
        return (b.lastName || '').localeCompare(a.lastName || '');
      } else if (this.sortAssignementProgress === this.SORT_ASC) {
        return (a.progressStatus || '').localeCompare(b.progressStatus || '');
      } else if (this.sortAssignementProgress === this.SORT_DESC) {
        return (b.progressStatus || '').localeCompare(a.progressStatus || '');
      } else if (this.sortCallsCompleted === this.SORT_ASC) {
        return (a.callsCompleted || 0) - (b.callsCompleted || 0);
      } else if (this.sortCallsCompleted === this.SORT_DESC) {
        return (b.callsCompleted || 0) - (a.callsCompleted || 0);
      } else if (this.sortPractices === this.SORT_ASC) {
        return (a.totalPractices || 0) - (b.totalPractices || 0);
      } else if (this.sortPractices === this.SORT_DESC) {
        return (b.totalPractices || 0) - (a.totalPractices || 0);
      } else if (this.sortMostRecentActivity === this.SORT_ASC) {
        return (a.recentActivity || '').localeCompare(b.recentActivity || '');
      } else if (this.sortMostRecentActivity === this.SORT_DESC) {
        return (b.recentActivity || '').localeCompare(a.recentActivity || '');
      }
      return 0;
    });
    this.teamMembers = sortData;
  }

  createLineChart(): void {
    if (this.lineChart) {
      this.lineChart.destroy(); // Destroy the existing chart
    }
    const ctx = document.getElementById('lineChart') as HTMLCanvasElement;
    this.lineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.lineChartLabels,
        datasets: [
          {
            data: this.lineChartDataSet,
            fill: true,
            tension: 0,
            borderColor: 'black',
            borderWidth: 1,
            pointBackgroundColor: '#D9D9D9',
            pointBorderColor: 'black',
            backgroundColor: (context) => {
              if (!context.chart.chartArea) {
                return;
              }
              const {
                ctx,
                data,
                chartArea: { top, bottom },
              } = context.chart;
              const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
              gradientBg.addColorStop(0, 'rgb(10,179,209)');
              gradientBg.addColorStop(1, '#FFFF');
              return gradientBg;
            },
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            grid: {
              display: false,
            },
            ticks: {
              stepSize: 25,
              precision: 0,
            },
          },
        },
      },
    });
  }

  openModal(event: MouseEvent, index: number): void {
    // Sending the Clicked number from Calls Completed Column event to Amplitude
    this.amplitudeService.trackEvent('myteams_click_callscompleted');

    const tableContainer = document.querySelector('#data') as HTMLElement;
    let bottomPosition = 0;
    let verticalPosition = event.y;
    let arrowDirection = 'my-teams-modal-arrow-up';

    if (tableContainer) {
      bottomPosition = this.getBottomPosition(tableContainer);
    }

    if (bottomPosition - verticalPosition > 140) {
      verticalPosition = verticalPosition + 20;
    } else {
      verticalPosition = verticalPosition - 150;
      arrowDirection = 'my-teams-modal-arrow-down';
    }

    const dialogPosition = {
      top: verticalPosition + 'px',
      left: event.x - 175 + 'px',
    };

    this.dialog.open(MyTeamModalComponent, {
      width: '349px',
      height: '132px',
      position: dialogPosition,
      panelClass: arrowDirection,
      hasBackdrop: true,
      backdropClass: 'transparent-backdrop',

      data: {
        callDetails: this.teamMembers[index].callDetails,
        summaryCalls: this.summaryCalls,
      },
    });
  }

  getBottomPosition(element: HTMLElement): number {
    const rect = element.getBoundingClientRect();
    return rect.bottom;
  }

  onSelectTeamsChange(event: any) {
    const selectedTeam = this.teams.findIndex(
      (team) => team.name === event.value
    );
    this.selectedTeam = this.teams[selectedTeam];

    // Sending the Clicked Team event to Amplitude
    this.amplitudeService.trackEvent('myteams_click_team', {
      team_name: this.selectedTeam.name,
    });

    this.teamUuid = this.teams[selectedTeam].teamUuid;
    localStorage.setItem('selected_team_uuid', this.selectedTeam.teamUuid);
    this.assignments = this.sortModuleAssignmentsByDueDate(
      this.teams[selectedTeam].moduleAssignments
    );
    if (!(this.assignments.length == 0)) {
      this.selectedModuleAssignment = this.assignments[0];
      this.moduleAssignmentUuid =
        this.selectedModuleAssignment.moduleAssignmentUuid;
      localStorage.setItem(
        'selected_module_assignment_uuid',
        this.selectedModuleAssignment.moduleAssignmentUuid
      );
      this.getModuleAssignmentSummary();
    } else {
      this.selectedModuleAssignment = null;
      this.moduleAssignmentUuid = '';
      localStorage.setItem('selected_module_assignment_uuid', '');
    }
  }

  onSelectModuleChange(event: any) {
    const selectedModule = this.assignments.findIndex(
      (teamModule) => teamModule.name === event.value
    );
    this.selectedModuleAssignment = this.assignments[selectedModule];

    // Sending the Clicked Module Assignment event to Amplitude
    this.amplitudeService.trackEvent('myteams_click_assignedmodule', {
      assignment_name: this.selectedModuleAssignment.name,
    });

    this.moduleAssignmentUuid =
      this.selectedModuleAssignment.moduleAssignmentUuid;
    localStorage.setItem(
      'selected_module_assignment_uuid',
      this.selectedModuleAssignment.moduleAssignmentUuid
    );
    this.getModuleAssignmentSummary();
  }

  private formatDate(dateString: string): string {
    return this.localeService.formatDateShort(dateString);
  }

  sortTeamsByName(teams: ITeamModuleAssignment[]): ITeamModuleAssignment[] {
    const locale = this.localeService.getCurrentLanguage() || 'en';
    return teams.sort((a, b) => {
      const nameA = localeUpperCase(a.name, locale);
      const nameB = localeUpperCase(b.name, locale);
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }

  sortModuleAssignmentsByDueDate(
    moduleAssignments: IAssignment[]
  ): IAssignment[] {
    if (Boolean(moduleAssignments)) {
      return moduleAssignments.sort((a, b) => {
        const dateA = new Date(a.dueDate);
        const dateB = new Date(b.dueDate);
        return dateA.getTime() - dateB.getTime();
      });
    } else {
      return [];
    }
  }

  isProgressStatusSameForAll(): boolean {
    return (
      this.teamMembers.length > 0 &&
      this.teamMembers.every(
        (member) => member.progressStatus === this.teamMembers[0].progressStatus
      )
    );
  }

  getSelectedTeamAndModuleAssignment(): void {
    this.teamUuid = localStorage.getItem('selected_team_uuid') || '';
    this.moduleAssignmentUuid =
      localStorage.getItem('selected_module_assignment_uuid') || '';
  }
}
